'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useCallback, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { getEventByAnalyticsId } from '../utils/eventLookup';
import { ANALYTICS_ID_ATTR } from '../config/analyticsAttributes';
import { collectExtraProperties } from '../utils/collectExtraProperties';
import { MTAG_EVENTS } from '../config/eventNames';
import { buildImpressionEvent } from '../utils/buildInteractionEvent';
import { getCurrentPageType } from '../config/pageConfig';
import { setConstructorBeaconUserId, clearConstructorBeaconUserId } from '../utils/constructorBeacon';
import { clearGtmUserTraits, setGtmUserTraits, type GtmUserTraits } from '../utils/gtmMiddleware';

// How long the page view waits for auth before it goes out without the user. A slow
// session check may delay the page view, never drop it.
const IDENTITY_WAIT_MS = 2000;
export type AnalyticsProduct = {
  sku?: string;
  name?: string;
  brand?: string;
};
type AnalyticsProviderProps = {
  product?: AnalyticsProduct;
  userId?: string;
  // Handed to GTM only (see gtmMiddleware). Segment gets its traits server-side.
  userTraits?: GtmUserTraits;
  // True once auth has confirmed nobody is signed in, as opposed to still loading.
  isSignedOut?: boolean;
  // True while the session is still being checked. The page view waits for it.
  isAuthLoading?: boolean;
  // Attach the signed-in user to the Constructor recommendation beacon. Shopper-app only —
  // the beacon is meaningless for the admin/brand portal, and setting an admin id on it would
  // ride admin identity onto the shopper recommendation data. Off by default.
  syncConstructorBeaconUser?: boolean;
};
type AnalyticsContextValue = {
  track: (eventName: string, extra?: Record<string, string | undefined>) => void;
  trackRegisteredClick: (analyticsId: string, extra?: {
    selectedValue?: string;
  }) => void;
  // The user is identified, or confirmed signed out, so events carry the right identity.
  isIdentityResolved: boolean;
};
const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);
export function AnalyticsProvider(t0) {
  const $ = _c(44);
  const {
    product,
    userId,
    userTraits,
    isSignedOut: t1,
    isAuthLoading: t2,
    syncConstructorBeaconUser: t3,
    children
  } = t0;
  const isSignedOut = t1 === undefined ? false : t1;
  const isAuthLoading = t2 === undefined ? false : t2;
  const syncConstructorBeaconUser = t3 === undefined ? false : t3;
  const {
    track: segmentTrack,
    identify,
    reset,
    getStoredUserId,
    isReady
  } = useAnalytics();
  const identifiedUserIdRef = useRef(null);
  const [isIdentityResolved, setIsIdentityResolved] = useState(false);
  product?.sku;
  product?.name;
  product?.brand;
  const t4 = product?.sku;
  const t5 = product?.name;
  const t6 = product?.brand;
  let t7;
  if ($[0] !== t4 || $[1] !== t5 || $[2] !== t6) {
    t7 = {
      product_id: t4,
      product_name: t5,
      brand: t6
    };
    $[0] = t4;
    $[1] = t5;
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const globalProperties = t7;
  let t8;
  if ($[4] !== globalProperties || $[5] !== segmentTrack) {
    t8 = (eventName, extra) => {
      segmentTrack(eventName, {
        ...globalProperties,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        ...extra
      });
    };
    $[4] = globalProperties;
    $[5] = segmentTrack;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const track = t8;
  let t10;
  let t9;
  if ($[7] !== identify || $[8] !== isReady || $[9] !== reset || $[10] !== syncConstructorBeaconUser || $[11] !== userId) {
    t9 = () => {
      if (!isReady) {
        return;
      }
      if (userId) {
        if (identifiedUserIdRef.current === userId) {
          return;
        }
        identifiedUserIdRef.current = userId;
        identify(userId);
        if (syncConstructorBeaconUser) {
          setConstructorBeaconUserId(userId);
        }
        return;
      }
      if (identifiedUserIdRef.current !== null) {
        identifiedUserIdRef.current = null;
        reset();
        if (syncConstructorBeaconUser) {
          clearConstructorBeaconUserId();
        }
      }
    };
    t10 = [isReady, userId, identify, reset, syncConstructorBeaconUser];
    $[7] = identify;
    $[8] = isReady;
    $[9] = reset;
    $[10] = syncConstructorBeaconUser;
    $[11] = userId;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[14] !== getStoredUserId || $[15] !== isAuthLoading || $[16] !== isReady || $[17] !== isSignedOut || $[18] !== reset) {
    t11 = () => {
      if (!isReady || isAuthLoading) {
        return;
      }
      if (!isSignedOut) {
        setIsIdentityResolved(true);
        return;
      }
      let cancelled = false;
      getStoredUserId().then(storedUserId => {
        if (cancelled) {
          return;
        }
        if (storedUserId) {
          reset();
        }
        setIsIdentityResolved(true);
      });
      return () => {
        cancelled = true;
      };
    };
    t12 = [isReady, isAuthLoading, isSignedOut, getStoredUserId, reset];
    $[14] = getStoredUserId;
    $[15] = isAuthLoading;
    $[16] = isReady;
    $[17] = isSignedOut;
    $[18] = reset;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      const timer = setTimeout(() => setIsIdentityResolved(true), IDENTITY_WAIT_MS);
      return () => clearTimeout(timer);
    };
    t14 = [];
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  const traitsEmail = userTraits?.email;
  const traitsFirstName = userTraits?.first_name;
  const traitsLastName = userTraits?.last_name;
  let t15;
  let t16;
  if ($[23] !== traitsEmail || $[24] !== traitsFirstName || $[25] !== traitsLastName || $[26] !== userId) {
    t15 = () => {
      if (!userId) {
        clearGtmUserTraits();
        return;
      }
      setGtmUserTraits({
        email: traitsEmail,
        first_name: traitsFirstName,
        last_name: traitsLastName
      });
    };
    t16 = [userId, traitsEmail, traitsFirstName, traitsLastName];
    $[23] = traitsEmail;
    $[24] = traitsFirstName;
    $[25] = traitsLastName;
    $[26] = userId;
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  useEffect(t15, t16);
  let t17;
  if ($[29] !== globalProperties || $[30] !== segmentTrack) {
    t17 = (analyticsId, selectedValue) => {
      const event = getEventByAnalyticsId(analyticsId);
      if (!event) {
        return;
      }
      const payload = buildImpressionEvent({
        impressionType: analyticsId,
        page: getCurrentPageType() ?? "",
        legacyComponent: event.interaction_context,
        legacySection: event.interaction_feature
      }, {
        sku: globalProperties.product_id,
        productName: globalProperties.product_name,
        brand: globalProperties.brand,
        selectedValue
      }, "click");
      segmentTrack(MTAG_EVENTS.IMPRESSION, {
        ...payload,
        analyticsId
      });
    };
    $[29] = globalProperties;
    $[30] = segmentTrack;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const emitRegisteredClick = t17;
  let t18;
  let t19;
  if ($[32] !== emitRegisteredClick) {
    t18 = () => {
      const handleClick = function handleClick(e) {
        const target = e.target as HTMLElement | null;
        if (!target) {
          return;
        }
        if (target instanceof HTMLInputElement && target.closest("label")) {
          return;
        }
        const tracked = target.closest(`[${ANALYTICS_ID_ATTR}]`);
        if (!tracked) {
          return;
        }
        const analyticsId_0 = tracked.getAttribute(ANALYTICS_ID_ATTR);
        if (!analyticsId_0) {
          return;
        }
        const extra_0 = collectExtraProperties(tracked);
        emitRegisteredClick(analyticsId_0, extra_0.selected_value);
      };
      document.addEventListener("click", handleClick, true);
      return () => document.removeEventListener("click", handleClick, true);
    };
    t19 = [emitRegisteredClick];
    $[32] = emitRegisteredClick;
    $[33] = t18;
    $[34] = t19;
  } else {
    t18 = $[33];
    t19 = $[34];
  }
  useEffect(t18, t19);
  let t20;
  if ($[35] !== emitRegisteredClick) {
    t20 = (analyticsId_1, extra_1) => emitRegisteredClick(analyticsId_1, extra_1?.selectedValue);
    $[35] = emitRegisteredClick;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const trackRegisteredClick = t20;
  let t21;
  if ($[37] !== isIdentityResolved || $[38] !== track || $[39] !== trackRegisteredClick) {
    t21 = {
      track,
      trackRegisteredClick,
      isIdentityResolved
    };
    $[37] = isIdentityResolved;
    $[38] = track;
    $[39] = trackRegisteredClick;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const value = t21;
  let t22;
  if ($[41] !== children || $[42] !== value) {
    t22 = <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
    $[41] = children;
    $[42] = value;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  return t22;
}
export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalyticsContext must be used within an AnalyticsProvider");
  }
  return context;
}

// For shared components that may render outside an AnalyticsProvider (tests,
// storybook, admin surfaces without product context) — tracking becomes a no-op.
export function useAnalyticsContextSafe() {
  return useContext(AnalyticsContext);
}